import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/apiClient';
import useRefetchOnHostingChange from '../hooks/useRefetchOnHostingChange';
import { getErrorMessage } from '../utils/errorMessage';
import { getLocale } from '../utils/dateLocale';
import { useNavigate } from 'react-router-dom';
import Menu from '../components/Menu';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card,
  CardContent,
  Tooltip,
  Popover
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import SendIcon from '@mui/icons-material/Send';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import GroupIcon from '@mui/icons-material/Group';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Функция для форматирования даты и времени
const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleString(getLocale(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (e) {
    return '—';
  }
};

export default function Alerts() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('alerts');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [isSaving, setIsSaving] = useState(false);

  const [messageDialog, setMessageDialog] = useState({ open: false, tab: 0 });
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);

  // Notification settings from backend - all disabled by default until loaded
  const [notificationSettings, setNotificationSettings] = useState({
    email_lvl: null,
    service_lvl: null,
    telegram_lvl: null,
    work_field: false,
    fan_field: false,
    temp_field: false,
    hashrate_field: false,
    bill_field: false,
    news_field: false,
    delay_between_alert: 600,
    number_operation: 1
  });
  const [telegramId, setTelegramId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [emailForNotifications, setEmailForNotifications] = useState('');

  // Message sending state
  const [recipientType, setRecipientType] = useState('user_id');
  const [owners, setOwners] = useState([]);
  const [hostings, setHostings] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUsersIds, setSelectedUsersIds] = useState([]);
  const [selectedHostingId, setSelectedHostingId] = useState('');
  const [selectedChannels, setSelectedChannels] = useState(['telegram', 'email', 'service']);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);

  // Notifications list for us
  const [notificationsList, setNotificationsList] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsFilterStatus, setNotificationsFilterStatus] = useState('unread');
  const [markingNotificationId, setMarkingNotificationId] = useState(null);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  // Fetch notification settings
  const fetchNotificationSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`${API_BASE_URL}user_notification`);
      const data = response?.data;
      if (data?.notification) {
        const notification = data.notification;
        // Convert null to false for boolean fields
        const normalizedSettings = {
          ...notification,
          work_field: notification.work_field ?? false,
          fan_field: notification.fan_field ?? false,
          temp_field: notification.temp_field ?? false,
          hashrate_field: notification.hashrate_field ?? false,
          bill_field: notification.bill_field ?? false,
          news_field: notification.news_field ?? false
        };
        setNotificationSettings(normalizedSettings);
      }
      if (data?.telegram_id) {
        setTelegramId(data.telegram_id);
      }

      // Load user email from localStorage
      const savedUserData = localStorage.getItem('userData');
      if (savedUserData) {
        try {
          const parsedUserData = JSON.parse(savedUserData);
          if (parsedUserData?.email) {
            setUserEmail(parsedUserData.email);
          }
        } catch (e) {
          console.error('Failed to parse userData');
        }
      }
    } catch (e) {
      setError(t('errorLoadSettings'));
    } finally {
      setLoading(false);
    }
  };

  // Save notification settings
  const saveNotificationSettings = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Prepare payload - send null for disabled channels, false for boolean fields
      const payload = {
        work_field: notificationSettings.work_field ?? false,
        fan_field: notificationSettings.fan_field ?? false,
        temp_field: notificationSettings.temp_field ?? false,
        hashrate_field: notificationSettings.hashrate_field ?? false,
        bill_field: notificationSettings.bill_field ?? false,
        news_field: notificationSettings.news_field ?? false,
        delay_between_alert: notificationSettings.delay_between_alert,
        number_operation: notificationSettings.number_operation,
        email_lvl: notificationSettings.email_lvl,
        service_lvl: notificationSettings.service_lvl,
        telegram_lvl: notificationSettings.telegram_lvl,
        telegram_id: telegramId || undefined
      };

      const response = await api.put(`${API_BASE_URL}user_notification`, payload);
      if (response?.data?.success) {
        setSuccess(t('settingsSaved'));
        if (response.data.notification) {
          const notification = response.data.notification;
          // Convert null to false for boolean fields
          const normalizedSettings = {
            ...notification,
            work_field: notification.work_field ?? false,
            fan_field: notification.fan_field ?? false,
            temp_field: notification.temp_field ?? false,
            hashrate_field: notification.hashrate_field ?? false,
            bill_field: notification.bill_field ?? false,
            news_field: notification.news_field ?? false
          };
          setNotificationSettings(normalizedSettings);
        }
        if (response.data.telegram_id !== undefined) {
          setTelegramId(response.data.telegram_id);
        }
      }
    } catch (e) {
      if (e.response?.status === 403) {
        setError(t('errorSubscriptionRequired'));
      } else {
        setError(t('errorSaveSettings'));
      }
    } finally {
      setIsSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  useEffect(() => {
    const loadUserRoleFromLocalStorage = () => {
      const savedUserData = localStorage.getItem('userData');
      if (savedUserData) {
        try {
          const parsedUserData = JSON.parse(savedUserData);
          setUserRole(parsedUserData?.role || 'user');
        } catch (e) {
          localStorage.removeItem('userData');
          setUserRole('user');
        }
      } else {
        setUserRole('user');
      }
    };

    loadUserRoleFromLocalStorage();

    const handleStorageChange = (event) => {
      if (event.key === 'userData') {
        loadUserRoleFromLocalStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Load recipients when message dialog opens
  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const resp = await api.get(`${API_BASE_URL}data/owner`);
        const results = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        setOwners(results);
      } catch (e) {
        setOwners([]);
      }
    };

    const fetchHostings = async () => {
      try {
        const resp = await api.get(`${API_BASE_URL}data/hosting`);
        const results = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        setHostings(results);
      } catch (e) {
        setHostings([]);
      }
    };

    if (messageDialog.open) {
      setIsLoadingRecipients(true);
      Promise.all([fetchOwners(), fetchHostings()]).finally(() => setIsLoadingRecipients(false));
    } else {
      // reset form when closing
      setRecipientType('user_id');
      setSelectedUserId('');
      setSelectedUsersIds([]);
      setSelectedHostingId('');
      setSelectedChannels(['telegram', 'email', 'service']);
      setMessageSubject('');
      setMessageBody('');
    }
  }, [messageDialog.open]);

  const fetchNotifications = async (readStatus) => {
    try {
      setNotificationsLoading(true);
      const params = {
        limit: 20,
        offset: 0,
        // Фильтр по важности не передаём: в ленте должны быть видны все события. Раньше сюда уходили старые
        // названия (notify, warning…), которых в данных нет, — лента критичных и важных алертов была пустой.
        read_status: readStatus || notificationsFilterStatus
      };
      const resp = await api.get(`${API_BASE_URL}notification`, {
        params,
        paramsSerializer: {
          indexes: null // This will format arrays as severity[]=notify&severity[]=warning
        }
      });
      const list = Array.isArray(resp?.data?.notifications) ? resp.data.notifications : [];
      setNotificationsList(list);
    } catch (e) {
      setNotificationsList([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [notificationsFilterStatus]);

  useRefetchOnHostingChange(fetchNotifications);

  const handleMarkRead = async (notificationId) => {
    if (!notificationId) return;
    try {
      setMarkingNotificationId(notificationId);
      // Optimistic update
      setNotificationsList((prev) => prev.map((n) => n.id === notificationId ? { ...n, read_status: 'read' } : n));
      await api.patch(`${API_BASE_URL}notification/${notificationId}`, { read_status: 'read' });
      if (notificationsFilterStatus === 'unread') {
        setNotificationsList((prev) => prev.filter((n) => n.id !== notificationId));
      }
    } catch (e) {
      await fetchNotifications();
      setError(t('errorMarkRead'));
    } finally {
      setMarkingNotificationId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setIsMarkingAll(true);
      // Optimistic update
      setNotificationsList((prev) => prev.map((n) => ({ ...n, read_status: 'read' })));
      // all: true — все непрочитанные уведомления пользователя, а не только загруженные 20
      await api.patch(`${API_BASE_URL}notifications`, { all: true, read_status: 'read' });
      await fetchNotifications();
    } catch (e) {
      await fetchNotifications();
      setError(t('errorMarkAllRead'));
    } finally {
      setIsMarkingAll(false);
    }
  };

  const isSendDisabled = () => {
    if (!messageSubject?.trim() || !messageBody?.trim()) return true;
    if (!selectedChannels?.length) return true;
    if (recipientType === 'user_id') return !selectedUserId;
    if (recipientType === 'users_id') return !selectedUsersIds?.length;
    if (recipientType === 'hosting_id') return !selectedHostingId;
    return false; // for 'all'
  };

  const isStepComplete = (step) => {
    if (step === 0) {
      // Recipients step
      if (recipientType === 'user_id') return !!selectedUserId;
      if (recipientType === 'users_id') return selectedUsersIds?.length > 0;
      if (recipientType === 'hosting_id') return !!selectedHostingId;
      if (recipientType === 'all') return true;
      return false;
    }
    if (step === 1) {
      // Channels step
      return selectedChannels?.length > 0;
    }
    if (step === 2) {
      // Message step
      return messageSubject?.trim() && messageBody?.trim();
    }
    return false;
  };

  const handleSendNotification = async () => {
    try {
      setError(null);
      if (!messageSubject?.trim() || !messageBody?.trim()) {
        setError(t('errorFillSubjectAndBody'));
        return;
      }

      if (!selectedChannels?.length) {
        setError(t('errorSelectChannel'));
        return;
      }

      const payload = {
        subject: messageSubject.trim(),
        body: messageBody.trim(),
        recipient_type: recipientType,
        channel: selectedChannels.length === 1 ? selectedChannels[0] : selectedChannels
      };

      if (recipientType === 'user_id' && selectedUserId) {
        payload.user_id = selectedUserId;
      } else if (recipientType === 'users_id' && Array.isArray(selectedUsersIds) && selectedUsersIds.length) {
        payload.users_id = selectedUsersIds;
      } else if (recipientType === 'hosting_id' && selectedHostingId) {
        payload.hosting_id = selectedHostingId;
      }

      // validate selection for specific recipient types
      if ((recipientType === 'user_id' && !payload.user_id) ||
          (recipientType === 'users_id' && !payload.users_id) ||
          (recipientType === 'hosting_id' && !payload.hosting_id)) {
        setError(t('selectRecipients'));
        return;
      }

      setIsSendingMessage(true);
      const response = await api.post(`${API_BASE_URL}notification`, payload);

      const count = response?.data?.count || 0;
      const recipientsCount = response?.data?.recipients_count || 0;
      const channelsCount = response?.data?.channels_count || 0;

      setSuccess(t('messageSentSuccess', { count, recipients: recipientsCount, channels: channelsCount }));
      setMessageDialog({ open: false, tab: 0 });
    } catch (e) {
      const errorMsg = getErrorMessage(e, t('errorSendMessage'));
      setError(errorMsg);
    } finally {
      setIsSendingMessage(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };


  const handleHelpClick = (event) => {
    setHelpAnchorEl(event.currentTarget);
  };

  const handleHelpClose = () => {
    setHelpAnchorEl(null);
  };

  const openHelp = Boolean(helpAnchorEl);

  return (
    <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Menu />
      <div style={{ padding: '20px' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('pageTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('pageSubtitle')}
          </Typography>

          {error && (
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'error.light' }}>
              <Typography color="error.contrastText">{error}</Typography>
            </Paper>
          )}

          {success && (
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'success.light' }}>
              <Typography color="success.contrastText">{success}</Typography>
            </Paper>
          )}
        </Box>

        {/* Notifications table section - above settings */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">{t('notifications')}</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                size="small"
                label={t('filterStatus')}
                value={notificationsFilterStatus}
                onChange={(e) => setNotificationsFilterStatus(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="unread">{t('filterUnread')}</MenuItem>
                <MenuItem value="read">{t('filterRead')}</MenuItem>
              </TextField>
              <Button variant="outlined" size="small" onClick={handleMarkAllRead} disabled={isMarkingAll || notificationsList.length === 0}>
                {isMarkingAll ? t('markingAll') : t('markAllRead')}
              </Button>
              <Button variant="outlined" size="small" onClick={() => fetchNotifications()} disabled={notificationsLoading}>
                {notificationsLoading ? t('refreshing') : t('refreshButton')}
              </Button>
            </Box>
          </Box>
          <Paper variant="outlined">
            <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('columnType')}</TableCell>
                  <TableCell>{t('columnSubject')}</TableCell>
                  <TableCell>{t('columnBody')}</TableCell>
                  <TableCell>{t('columnTime')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationsList.map((n) => (
                  <TableRow key={n.id} onClick={() => handleMarkRead(n.id)} sx={{ cursor: 'pointer', backgroundColor: n.read_status === 'unread' ? ((t) => t.palette.action.hover) : 'inherit' }}>
                    <TableCell>{n.type}</TableCell>
                    <TableCell>{n.subject}</TableCell>
                    <TableCell>{n.body}</TableCell>
                    <TableCell>{formatDateTime(n.created_at)}</TableCell>
                  </TableRow>
                ))}
                {!notificationsLoading && notificationsList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">{t('noNotifications')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </TableContainer>
          </Paper>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <NotificationsActiveIcon sx={{ mr: 1 }} />
            {t('notificationSettings')}
          </Typography>

          {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}

          <Box sx={{ display: 'flex', gap: 8, mb: 3, flexWrap: 'wrap' }}>
            {/* Left Column */}
            <Box sx={{ flex: 1, minWidth: '300px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('channelsTitle')}
              </Typography>

              {/* Service Card */}
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsActiveIcon color="primary" />
                      <Typography variant="h6">{t('channelSite')}</Typography>
                    </Box>
                    <Switch
                      checked={notificationSettings.service_lvl !== null}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNotificationSettings(prev => ({ ...prev, service_lvl: 3 }));
                        } else {
                          setNotificationSettings(prev => ({ ...prev, service_lvl: null }));
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Email Card */}
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: notificationSettings.email_lvl !== null ? 2 : 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ color: '#EA4335' }} />
                      <Typography variant="h6">{t('channelEmail')}</Typography>
                    </Box>
                    <Switch
                      checked={notificationSettings.email_lvl !== null}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNotificationSettings(prev => ({ ...prev, email_lvl: 3 }));
                        } else {
                          setNotificationSettings(prev => ({ ...prev, email_lvl: null }));
                        }
                      }}
                    />
                  </Box>
                  {notificationSettings.email_lvl !== null && (
                    <>
                      {userEmail && (
                        <TextField
                          fullWidth
                          size="small"
                          label={t('emailForNotifications')}
                          value={userEmail}
                          disabled
                        />
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Telegram Card */}
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: notificationSettings.telegram_lvl !== null ? 2 : 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TelegramIcon sx={{ color: '#0088cc' }} />
                      <Typography variant="h6">{t('channelTelegram')}</Typography>
                    </Box>
                    <Switch
                      checked={notificationSettings.telegram_lvl !== null}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNotificationSettings(prev => ({ ...prev, telegram_lvl: 3 }));
                        } else {
                          setNotificationSettings(prev => ({ ...prev, telegram_lvl: null }));
                        }
                      }}
                    />
                  </Box>
                  {notificationSettings.telegram_lvl !== null && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2">{t('telegramIdLabel')}</Typography>
                        <IconButton size="small" onClick={handleHelpClick}>
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('telegramIdField')}
                        value={telegramId}
                        onChange={(e) => setTelegramId(e.target.value)}
                        placeholder={t('telegramIdPlaceholder')}
                      />
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Right Column */}
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              {/* Boolean Switches */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                  {t('typesTitle')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.work_field}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, work_field: e.target.checked }))}
                      />
                    }
                    label={t('typeOnlineOffline')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.fan_field}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, fan_field: e.target.checked }))}
                      />
                    }
                    label={t('typeFanProblems')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.temp_field}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, temp_field: e.target.checked }))}
                      />
                    }
                    label={t('typeTempProblems')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.hashrate_field}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, hashrate_field: e.target.checked }))}
                      />
                    }
                    label={t('typeHashrateProblems')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.bill_field}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, bill_field: e.target.checked }))}
                      />
                    }
                    label={t('typeBilling')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.news_field}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, news_field: e.target.checked }))}
                      />
                    }
                    label={t('typeHostingNews')}
                  />
                </Box>
              </Box>

              {/* Delay and Occurrences */}
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('delayBetweenAlerts')}
                  value={notificationSettings.delay_between_alert}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, delay_between_alert: Number(e.target.value) || 0 }))}
                  helperText={t('delayHelperText')}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('operationCount')}
                  value={notificationSettings.number_operation}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, number_operation: Number(e.target.value) || 1 }))}
                  inputProps={{ min: 1, max: 9 }}
                  helperText={t('operationCountHelperText')}
                />
              </Box>
            </Box>
          </Box>

          {/* Save Button */}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={saveNotificationSettings}
              disabled={isSaving}
              size="large"
            >
              {isSaving ? t('saving') : t('saveSettings')}
            </Button>
          </Box>
        </Paper>

        {/* Help Popover */}
        <Popover
          open={openHelp}
          anchorEl={helpAnchorEl}
          onClose={handleHelpClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 3, maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>{t('howToGetTelegramId')}</Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              {t('method1Title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('method1Step1')} <strong>@waymorr_alerts_bot</strong><br/>
              {t('method1Step2')}<br/>
              {t('method1Step3')}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              {t('method2Title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('method2Step1')} <strong>@waymorr_alerts_bot</strong><br/>
              {t('method2Step2')}<br/>
              {t('method2Step3')}<br/>
              {t('method2Step4')}
            </Typography>
          </Box>
        </Popover>

        {(userRole === 'admin' || userRole === 'manager') && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" startIcon={<SendIcon />} onClick={() => setMessageDialog({ open: true, tab: 0 })}>
                {t('sendMessage')}
              </Button>
            </Box>
          </Paper>
        )}

        <Dialog open={messageDialog.open} onClose={() => setMessageDialog({ open: false, tab: 0 })} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SendIcon />
            {t('sendMessageTitle')}
          </DialogTitle>
          <DialogContent>
            <Tabs
              value={messageDialog.tab}
              onChange={(e, newValue) => setMessageDialog(prev => ({ ...prev, tab: newValue }))}
              sx={{ mb: 3 }}
            >
              <Tab
                label={t('recipientsTab')}
                icon={<GroupIcon />}
                disabled={false}
              />
              <Tab
                label={t('channelsTab')}
                icon={<NotificationsActiveIcon />}
                disabled={!isStepComplete(0)}
              />
              <Tab
                label={t('messageTab')}
                icon={<EmailIcon />}
                disabled={!isStepComplete(0) || !isStepComplete(1)}
              />
            </Tabs>
            {messageDialog.tab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('recipientTypeLabel')}</Typography>
                  <RadioGroup
                    row
                    value={recipientType}
                    onChange={(e) => setRecipientType(e.target.value)}
                  >
                    <FormControlLabel value="user_id" control={<Radio />} label={t('recipientUser')} />
                    <FormControlLabel value="users_id" control={<Radio />} label={t('recipientUserGroup')} />
                    <FormControlLabel value="hosting_id" control={<Radio />} label={t('recipientHosting')} />
                    <FormControlLabel value="all" control={<Radio />} label={t('recipientAll')} />
                  </RadioGroup>
                </FormControl>

                {recipientType === 'user_id' && (
                  <Autocomplete
                    loading={isLoadingRecipients}
                    options={owners}
                    getOptionLabel={(o) => o?.name || ''}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    value={owners.find((o) => o.id === selectedUserId) || null}
                    onChange={(e, val) => setSelectedUserId(val?.id || '')}
                    renderInput={(params) => (
                      <TextField {...params} label={t('selectUser')} fullWidth size="small" InputProps={{ ...params.InputProps, endAdornment: (<>{isLoadingRecipients ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
                    )}
                  />
                )}

                {recipientType === 'users_id' && (
                  <Autocomplete
                    multiple
                    loading={isLoadingRecipients}
                    options={owners}
                    getOptionLabel={(o) => o?.name || ''}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    value={owners.filter((o) => selectedUsersIds.includes(o.id))}
                    onChange={(e, val) => setSelectedUsersIds(val.map((x) => x.id))}
                    renderInput={(params) => (
                      <TextField {...params} label={t('selectUsers')} fullWidth size="small" InputProps={{ ...params.InputProps, endAdornment: (<>{isLoadingRecipients ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
                    )}
                  />
                )}

                {recipientType === 'hosting_id' && (
                  <Autocomplete
                    loading={isLoadingRecipients}
                    options={hostings}
                    getOptionLabel={(o) => o?.name || ''}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    value={hostings.find((h) => h.id === selectedHostingId) || null}
                    onChange={(e, val) => setSelectedHostingId(val?.id || '')}
                    renderInput={(params) => (
                      <TextField {...params} label={t('selectHosting')} fullWidth size="small" InputProps={{ ...params.InputProps, endAdornment: (<>{isLoadingRecipients ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
                    )}
                  />
                )}

                {recipientType === 'all' && (
                  <Typography variant="body2" color="text.secondary">{t('willBeSentToAll')}</Typography>
                )}
              </Box>
            )}
            {messageDialog.tab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2">{t('selectChannelsTitle')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('channelsNote')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedChannels.includes('service')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedChannels(prev => [...prev, 'service']);
                          } else {
                            setSelectedChannels(prev => prev.filter(c => c !== 'service'));
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NotificationsActiveIcon color="primary" />
                        <Typography>{t('channelOnSite')}</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedChannels.includes('email')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedChannels(prev => [...prev, 'email']);
                          } else {
                            setSelectedChannels(prev => prev.filter(c => c !== 'email'));
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ color: '#EA4335' }} />
                        <Typography>{t('channelEmailIfConfigured')}</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedChannels.includes('telegram')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedChannels(prev => [...prev, 'telegram']);
                          } else {
                            setSelectedChannels(prev => prev.filter(c => c !== 'telegram'));
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TelegramIcon sx={{ color: '#0088cc' }} />
                        <Typography>{t('channelTelegramIfConfigured')}</Typography>
                      </Box>
                    }
                  />
                </Box>
                {selectedChannels.length === 0 && (
                  <Typography variant="body2" color="error">
                    {t('selectAtLeastOneChannel')}
                  </Typography>
                )}
              </Box>
            )}
            {messageDialog.tab === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('subjectField')}
                  value={messageSubject}
                  onChange={(e) => setMessageSubject(e.target.value)}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label={t('messageBodyField')}
                  placeholder={t('messageBodyPlaceholder')}
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
            <Button onClick={() => setMessageDialog({ open: false, tab: 0 })}>{t('common:cancel')}</Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {messageDialog.tab > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setMessageDialog(prev => ({ ...prev, tab: prev.tab - 1 }))}
                >
                  {t('common:back')}
                </Button>
              )}
              {messageDialog.tab < 2 ? (
                <Button
                  variant="contained"
                  onClick={() => setMessageDialog(prev => ({ ...prev, tab: prev.tab + 1 }))}
                  disabled={!isStepComplete(messageDialog.tab)}
                >
                  {t('common:next')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSendNotification}
                  disabled={isSendingMessage || isSendDisabled()}
                >
                  {isSendingMessage ? t('sending') : t('common:send')}
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>

      </div>
    </div>
  );
}


